import type { ExtendedProductMainOffer } from 'src/areas/product/components/product-context';
import type { ProductMainOffer } from 'src/types';
import type { PriceDisplay } from './types';
import { PriceDisplayEnum } from './types';

const ALMOST_INFINATE_SHIPPING_COST = 1000000;

export enum SortDirection {
  Ascending,
  Descending,
}

interface IPropertyResolver<T> {
  resolver: (offer: ProductMainOffer) => T;
  sortDirection: SortDirection;
}

const sortByPriceProp = (priceDisplay: PriceDisplay) => {
  const resolver =
    priceDisplay === PriceDisplayEnum.PRICE_INCLUDES_SHIPPING
      ? (offer: ProductMainOffer) => {
          return getPriceInclShipping(offer) || offer?.price?.exclShipping;
        }
      : (offer: ProductMainOffer) => offer.price && offer.price.exclShipping;

  return { resolver, sortDirection: SortDirection.Ascending };
};

const sortByShippingPriceProp = () => {
  const resolver = (offer: ProductMainOffer) => getCheapestShippingCost(offer);
  return { resolver, sortDirection: SortDirection.Ascending };
};

const sortByStockStatusProp = () => {
  const resolver = (offer: ProductMainOffer) =>
    offer.stock?.status === 'in_stock' ? 0 : 1;
  return { resolver, sortDirection: SortDirection.Ascending };
};

const sortByUserReviewProp = () => {
  const resolver = (offer: ProductMainOffer) =>
    offer.store?.userReviewSummary?.rating || 0;
  return { resolver, sortDirection: SortDirection.Descending };
};

const sortByUserReviewCountProp = () => {
  const resolver = (offer: ProductMainOffer) =>
    offer.store?.userReviewSummary?.countTotal || 0;
  return { resolver, sortDirection: SortDirection.Descending };
};

const getPriceInclShipping = (offer: ProductMainOffer): number | undefined => {
  const basePrice = offer.price?.exclShipping ?? Infinity;
  const cheapestShipping = getCheapestShippingCost(offer);
  return basePrice + cheapestShipping;
};

const getCheapestShippingCost = (offer: ProductMainOffer): number => {
  return (
    offer.shipping?.cheapest?.shippingCost ?? ALMOST_INFINATE_SHIPPING_COST
  );
};

export const sortOffersByPrice = (
  priceDisplay: PriceDisplay,
  prices: ExtendedProductMainOffer[]
) => {
  const priceSort = sortByPriceProp(priceDisplay);
  const shippingPriceProp = sortByShippingPriceProp();
  const stockStatusProp = sortByStockStatusProp();
  const userReviewProp = sortByUserReviewProp();
  const userReviewCountProp = sortByUserReviewCountProp();
  return genericSort(
    prices,
    priceSort,
    shippingPriceProp,
    stockStatusProp,
    userReviewProp,
    userReviewCountProp
  );
};

export const genericSort = <T, TU>(
  prices: ExtendedProductMainOffer[],
  primaryProp: IPropertyResolver<T>,
  secondaryProp?: IPropertyResolver<TU>,
  thirdProp?: IPropertyResolver<TU>,
  fouthProp?: IPropertyResolver<TU>,
  fifthProp?: IPropertyResolver<TU>
) => {
  return [...prices].sort((a, b) => {
    const firstSort = orderItem(a, b, primaryProp);
    if (firstSort !== 0) {
      return firstSort;
    }

    if (secondaryProp) {
      const secondSort = orderItem(a, b, secondaryProp);
      if (secondSort !== 0) {
        return secondSort;
      }
    }

    if (thirdProp) {
      const thirdSort = orderItem(a, b, thirdProp);
      if (thirdSort !== 0) {
        return thirdSort;
      }
    }

    if (fouthProp) {
      const fourthSort = orderItem(a, b, fouthProp);
      if (fourthSort !== 0) {
        return fourthSort;
      }
    }

    if (fifthProp) {
      const fifthSort = orderItem(a, b, fifthProp);
      if (fifthSort !== 0) {
        return fifthSort;
      }
    }

    return 0;
  });
};

const orderItem = <T>(
  a: ProductMainOffer,
  b: ProductMainOffer,
  prop: IPropertyResolver<T>
) => {
  const aProp = prop.resolver(a);
  const bProp = prop.resolver(b);

  if (aProp < bProp) {
    return prop.sortDirection === SortDirection.Descending ? 1 : -1;
  }

  if (aProp > bProp) {
    return prop.sortDirection === SortDirection.Descending ? -1 : 1;
  }

  return 0;
};
